// src/pages/ProtocolsPage.js
//
// Phase 9 — Protocol → SOA extraction: per-study protocol upload + list.
// Route: /sourcex/protocols/:studyId (flag-gated in App.js). This is STEP 1 of
// the three that build a study's source documents.
//
// The page is built around one question: what should the person who just landed
// here actually do?
//
//   • Nothing uploaded yet          -> one big button in the middle. Nothing else.
//   • Just uploaded                 -> a modal that watches the extraction and
//                                      hands them the Review button the moment
//                                      there is something to review.
//   • Already uploaded / already
//     built the source documents    -> a modal saying so, offering the step
//                                      they actually want, with re-uploading as
//                                      the quiet second option.
//
// Landing on the wrong page is the normal case, not the exceptional one: this
// is step 1 of 3 and it is reachable from the home page at any time. So the
// route onward is offered up front and stays pinned at the foot of the page
// after the modal is dismissed.
//
// Nothing about the upload or extraction pipeline changed — same
// uploadAndCreateProtocol, same startExtraction, same retry, same review route.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ScanSearch,
  X,
  ChevronRight,
} from "lucide-react";
import { db } from "../firebase";
import { getSourcexTemplatesPath } from "../services/firestorePaths";
import {
  uploadAndCreateProtocol,
  startExtraction,
  retryExtraction,
  listenProtocols,
} from "../services/protocolExtractionService";
import { listenEcrfs } from "../services/ecrfExtractionService";
import {
  isReviewable,
  needsReview,
  nextWorkflowStep,
} from "../services/sourcexWorkflow";
import StepProgressOverlay from "../components/sourcex/StepProgressOverlay";
import { PROTOCOL_STATUS } from "../services/protocolTypes";
import { PROTOCOL_COMPARE_ENABLED } from "../config/featureFlags";
import { useTenant } from "../context/TenantContext";
import { effectiveRole } from "../services/membershipService";
import { listenVersions, listenVersionRequests, answerNewVersion, logProtocolEvent } from "../services/protocolVersionService";
import { newVersionQuestion, versionLabel, sourcexVersionGuidance } from "../services/protocolVersions";
import NewVersionDialog from "../components/study/NewVersionDialog";

// The extraction function is capped at 540s. If it is killed at that wall — a
// hard timeout, not a thrown error — nothing runs to write a status, so the
// protocol is left saying "processing" forever with no way out: Retry is only
// offered on `failed` and `uploaded`.
//
// Past this many minutes, a "processing" protocol cannot still be running, so
// it is stalled and Retry is offered. The margin over 9 minutes is deliberate,
// so a genuinely-running extraction is never restarted underneath itself.
const STALLED_AFTER_MS = 12 * 60 * 1000;

const isStalled = (p) => {
  if (p?.status !== PROTOCOL_STATUS.PROCESSING) return false;
  const started = p.processingStartedAt;
  // No timestamp at all (an older document, or the write has not landed yet)
  // is not evidence of a stall — leave it alone.
  if (!started) return false;
  const t = typeof started?.toDate === "function" ? started.toDate() : new Date(started);
  const ms = t.getTime();
  return Number.isFinite(ms) && Date.now() - ms > STALLED_AFTER_MS;
};

const STATUS_STYLES = {
  [PROTOCOL_STATUS.UPLOADED]: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  [PROTOCOL_STATUS.PROCESSING]: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  [PROTOCOL_STATUS.NEEDS_REVIEW]: "bg-amber-500/15 text-amber-200 border-amber-400/30",
  [PROTOCOL_STATUS.APPROVED]: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  [PROTOCOL_STATUS.FAILED]: "bg-red-500/15 text-red-200 border-red-400/30",
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border ${
      STATUS_STYLES[status] || STATUS_STYLES[PROTOCOL_STATUS.UPLOADED]
    }`}
  >
    {status === PROTOCOL_STATUS.PROCESSING && <Loader2 className="w-3 h-3 animate-spin" />}
    {status === PROTOCOL_STATUS.APPROVED && <CheckCircle2 className="w-3 h-3" />}
    {status === PROTOCOL_STATUS.FAILED && <AlertTriangle className="w-3 h-3" />}
    {(status || "uploaded").replace(/_/g, " ")}
  </span>
);

/**
 * "12 Sep 2026, 14:05" from whatever the field happens to hold.
 *
 * `uploadedAt` is a Firestore serverTimestamp, which is NULL for the moment
 * between the local write and the server's acknowledgement — a just-uploaded
 * protocol therefore has no time yet and must not render "Invalid Date". Older
 * documents predate the field entirely and fall back to `createdAt`.
 */
const formatWhen = (value) => {
  if (!value) return "";
  const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/**
 * Which pages of the PDF the schedule of assessments was found on.
 *
 * A chip, not a run of grey text beside the status: it is the one EXTRACTED
 * fact on the row — what the pipeline actually found — and the first thing to
 * check when an extraction looks wrong. Sky, so it cannot be mistaken for the
 * status badge next to it, which is state rather than data.
 */
const SoaPagesChip = ({ pages }) =>
  pages?.length > 0 ? (
    <span
      title={`Schedule of assessments found on page${
        pages.length === 1 ? "" : "s"
      } ${pages.join(", ")}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-sky-400/25 bg-sky-400/10 text-[11px] text-sky-200"
    >
      <span className="uppercase tracking-wider text-[9px] font-semibold text-sky-300/80">
        SOA
      </span>
      p.{pages.join(", ")}
    </span>
  ) : null;

/**
 * Who uploaded it, and when — the two facts a monitor asks about a file.
 *
 * Its own quiet line UNDER the badges rather than a third grey fragment beside
 * them. It is provenance, not status: read once when someone asks "who put this
 * here?", never scanned. The email is the brighter half because it is the half
 * people actually search the row for.
 */
const UploadedLine = ({ protocol }) => {
  const when = formatWhen(protocol.uploadedAt || protocol.createdAt);
  const who = protocol.createdBy || "";
  if (!when && !who) return null;
  return (
    <div className="mt-1.5 text-[10px] text-slate-500">
      Uploaded {when || "just now"}
      {who && (
        <>
          {" by "}
          <span className="text-slate-400">{who}</span>
        </>
      )}
    </div>
  );
};

/** The hidden-input upload control, as whatever the caller wants it to look
 * like. Two call sites need it (the empty-state hero and the header), and a
 * file input cannot be shared between them. */
function UploadButton({ busy, onFile, className, children }) {
  return (
    <label className={`${className} ${busy ? "cursor-wait" : "cursor-pointer"}`}>
      {children}
      <input
        type="file"
        accept="application/pdf"
        className="hidden"
        disabled={busy}
        onChange={onFile}
      />
    </label>
  );
}

/** A centred dialog. Escape and the backdrop close it; there is always a real
 * way out, because every modal on this page is advisory. */
function Modal({ onClose, title, children, footer }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ y: 8, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        className="w-full max-w-md rounded-2xl border border-white/12 bg-[#0c0826] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.9)] overflow-hidden"
      >
        <div className="flex items-start gap-3 px-5 pt-5">
          <h2 className="min-w-0 flex-1 text-base font-semibold text-white leading-snug">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children && <div className="px-5 pt-2 text-sm text-slate-300">{children}</div>}
        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 mt-3 border-t border-white/10 bg-white/[0.02]">
          {footer}
        </div>
      </motion.div>
    </motion.div>
  );
}

const primaryBtn =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition";
// One rung down from primary and still clearly a real action — for the second
// thing genuinely worth doing, beside the first.
const secondaryBtn =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-sm font-semibold text-white hover:brightness-110 transition";
const quietBtn =
  "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.05] border border-white/12 text-sm text-slate-300 hover:bg-white/[0.1] hover:text-white transition";

export default function ProtocolsPage() {
  const { studyId } = useParams();
  const navigate = useNavigate();
  // null while the first snapshot is still in flight — distinct from [], which
  // means "confirmed nothing here". The landing modal must not fire on a study
  // whose protocols simply have not arrived yet.
  const [protocols, setProtocols] = useState(null);
  const [ecrfs, setEcrfs] = useState(null);
  const [hasAssembled, setHasAssembled] = useState(false);
  const [assembledChecked, setAssembledChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The protocol uploaded in THIS visit. Drives the "now review it" modal, which
  // follows that one protocol's extraction rather than the list as a whole.
  const [justUploadedId, setJustUploadedId] = useState(null);
  const [landingOpen, setLandingOpen] = useState(false);
  // Protocol versions (flag PROTOCOL_COMPARE_ENABLED): every upload on a study
  // that already has a protocol asks "Is this a new version?". An admin's yes
  // makes it the next version; anyone else's goes to an admin to approve.
  // Reading it here (the extraction below) is unchanged either way.
  const { access, tenantId, siteId } = useTenant();
  const role = effectiveRole(access, tenantId, siteId);
  const isAdmin = role === "admin" || role === "owner" || Boolean(access?.isOwner);
  const [versions, setVersions] = useState([]);
  const [versionRequests, setVersionRequests] = useState([]);
  const [askVersion, setAskVersion] = useState(null); // { file, current, next }
  const [versionNote, setVersionNote] = useState("");
  useEffect(() => {
    if (!PROTOCOL_COMPARE_ENABLED || !studyId) return undefined;
    const offs = [
      listenVersions(studyId, setVersions, () => setVersions([])),
      listenVersionRequests(studyId, setVersionRequests, () => setVersionRequests([])),
    ];
    return () => offs.forEach((off) => off && off());
  }, [studyId]);

  useEffect(() => {
    if (!studyId) return undefined;
    const unsub = listenProtocols(studyId, setProtocols, (e) => {
      setError(e.message || "Failed to load protocols.");
      // Out of `null` even on failure, or the page sits on "Loading…" for ever
      // with the error banner above it and no way to upload anything.
      setProtocols([]);
    });
    return () => unsub && unsub();
  }, [studyId]);

  // The other two steps' state, read only to answer "where should this person
  // be instead?". Failures are swallowed: not knowing what comes next is a
  // reason to offer less guidance, never a reason to break step 1.
  useEffect(() => {
    if (!studyId) return undefined;
    const unsub = listenEcrfs(
      studyId,
      setEcrfs,
      () => setEcrfs([])
    );
    return () => unsub && unsub();
  }, [studyId]);

  useEffect(() => {
    if (!studyId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(db, getSourcexTemplatesPath(), `${studyId}__assembled`)
        );
        if (!cancelled) setHasAssembled(snap.exists());
      } catch (_) {
        /* treated as "not built" — see above */
      } finally {
        if (!cancelled) setAssembledChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  const loading = protocols === null || ecrfs === null || !assembledChecked;

  // Where this person probably meant to go. The ordering lives in
  // services/sourcexWorkflow so all three step pages answer it identically —
  // two pages pointing one coordinator in two directions is worse than neither
  // pointing anywhere.
  // Protocol versions: the current version comes first; an older one is
  // locked (no Review, Approve or Read) until documents are built from it.
  const guidance = useMemo(
    () => (PROTOCOL_COMPARE_ENABLED ? sourcexVersionGuidance(protocols || [], versions, { docsBuilt: hasAssembled }) : null),
    [protocols, versions, hasAssembled]
  );
  const nextStep = useMemo(
    () =>
      nextWorkflowStep({
        studyId,
        protocols: protocols || [],
        ecrfs: ecrfs || [],
        hasAssembled,
        // On this page the highlighted "Read this protocol" row IS that step.
        currentPath: guidance?.readFirst ? `/sourcex/protocols/${studyId}` : "",
        isLocked: guidance?.isLocked || null,
        readFirst: guidance?.readFirst || null,
      }),
    [hasAssembled, protocols, ecrfs, studyId, guidance]
  );

  // "You have already done this" — raised ONCE, on the first load that knows
  // enough to say so. A ref rather than state so re-renders (and the live
  // snapshots behind them) cannot reopen a modal the user has closed.
  const landingShown = useRef(false);
  useEffect(() => {
    if (landingShown.current || loading) return;
    landingShown.current = true;
    if ((protocols || []).length > 0 || hasAssembled) setLandingOpen(true);
  }, [loading, protocols, hasAssembled]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (PROTOCOL_COMPARE_ENABLED) {
      const q = newVersionQuestion(versions, protocols || []);
      if (q.ask) {
        setLandingOpen(false);
        setAskVersion({ file, current: q.current, next: q.next });
        return;
      }
    }
    await uploadProtocolFile(file, null);
  };

  // newVersion: true / false = the answer to "Is this a new version?"; null = not asked.
  const uploadProtocolFile = async (file, newVersion) => {
    const next = askVersion?.next;
    // A study's first protocol: Version 1 (an admin's at once; anyone else's
    // waits for an admin to approve it). The reading below runs as always.
    const first = PROTOCOL_COMPARE_ENABLED && newVersion === null && (protocols || []).length === 0;
    setAskVersion(null);
    setVersionNote("");
    setError("");
    setBusy(true);
    // Whatever brought them here, they have now chosen to upload — a stale
    // "already uploaded?" modal reopening over the result would be nonsense.
    setLandingOpen(false);
    try {
      const { protocolId } = await uploadAndCreateProtocol(studyId, file);
      setJustUploadedId(protocolId);
      // Fire-and-forget extraction; the list updates live via the snapshot.
      startExtraction(studyId, protocolId).catch((err) =>
        setError(err.message || "Extraction could not be started.")
      );
      if (first) {
        setVersionNote(isAdmin ? "Saved as Version 1 of the protocol." : "Saved. This will be Version 1 of the protocol once an admin approves it.");
        answerNewVersion({ yes: true, isAdmin, studyKey: studyId, protocolId, fileName: file.name, source: "sourcex" }).catch((err) =>
          setError(err.message || "Couldn't save the version.")
        );
      }
      if (newVersion) {
        setVersionNote(isAdmin ? `Saved as Version ${next}.` : `Sent to an admin to approve as Version ${next}.`);
        answerNewVersion({ yes: true, isAdmin, studyKey: studyId, protocolId, fileName: file.name, source: "sourcex" }).catch((err) =>
          setError(err.message || "Couldn't save the new version.")
        );
      }
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async (protocolId) => {
    setError("");
    try {
      await retryExtraction(studyId, protocolId);
    } catch (err) {
      setError(err.message || "Retry failed.");
    }
  };

  const list = protocols || [];
  const uploaded = list.find((p) => p.id === justUploadedId) || null;

  // The protocol this study already has, whatever state it is in.
  // `listenProtocols` orders newest-first, so the first entry is it.
  //
  // Needed SEPARATELY from `awaitingReview` below, which only counts a protocol
  // once its schedule has been read. A protocol still being extracted — very
  // often one uploaded minutes earlier from the study page's Screening Visit
  // card, which writes this same collection — satisfied neither that nor
  // `nextWorkflowStep`, so the landing modal fell through to "Step 2 · Upload
  // Blank eCRF" and left Re-upload as the only thing to press. For the several
  // minutes an extraction runs, the page offered no way to engage with the
  // protocol that was already there.
  const existing = list[0] || null;
  const existingRoute = existing ? `/sourcex/protocols/${studyId}/${existing.id}` : "";
  const existingIsReading =
    existing?.status === PROTOCOL_STATUS.UPLOADED ||
    existing?.status === PROTOCOL_STATUS.PROCESSING;
  const existingWho = existing?.createdBy || "";
  const existingWhen = existing ? formatWhen(existing.uploadedAt || existing.createdAt) : "";

  // The most recent protocol still waiting on a human. `listenProtocols` orders
  // newest-first, so the first match IS the latest one.
  //
  // Offered in the landing modal IN ADDITION to the next step, not instead of
  // it: once the source documents are built, nextWorkflowStep rightly points at
  // those, and an unreviewed protocol sitting underneath them would never get
  // mentioned anywhere. Skipped when the next step already IS this review, so
  // the modal never shows the same button twice.
  // `needsReview`, not `isReviewable`: an APPROVED protocol can still be opened
  // and looked at, but it is finished work and nothing is waiting on anybody.
  const awaitingReview = list.find((p) => needsReview(p) && !(guidance && guidance.isLocked(p))) || null;
  const reviewRoute = awaitingReview
    ? `/sourcex/protocols/${studyId}/${awaitingReview.id}`
    : "";
  const offerReview = !!reviewRoute && nextStep.to !== reviewRoute;

  // Uploading, or the server is still reading it. `uploaded` is the protocol
  // from THIS visit, so the overlay follows the one the coordinator just
  // started and not some other protocol's status changing underneath them.
  const working =
    busy ||
    (!!uploaded &&
      (uploaded.status === PROTOCOL_STATUS.UPLOADED ||
        uploaded.status === PROTOCOL_STATUS.PROCESSING));
  // The overlay owns the whole in-flight period AND its failure; the modal
  // below owns only the outcome worth acting on. Two surfaces describing the
  // same failed extraction would appear one on top of the other.
  const uploadFailed = uploaded?.status === PROTOCOL_STATUS.FAILED;

  return (
    <div className="sourcex-page min-h-screen bg-[#0b0820] text-slate-100 font-[Manrope] px-4 py-6">
      {/* NO "keep this tab open" here, deliberately. extractProtocolSoa is
          fire-and-forget on the server and finishes whether or not this page is
          open — the list picks it up from the snapshot on the way back. Warning
          about it anyway would be false, and a rule that is sometimes false is a
          rule people learn to ignore on the step where it is true (step 2). */}
      <StepProgressOverlay
        open={working || uploadFailed}
        title="Reading the protocol"
        detail={busy ? "Uploading…" : "Finding the schedule of assessments"}
        typical="usually a few minutes"
        error={
          uploadFailed
            ? uploaded.errorMessage ||
              "The schedule of assessments could not be read."
            : ""
        }
        retryLabel="Try again"
        onRetry={() => uploaded && handleRetry(uploaded.id)}
        onDismiss={() => setJustUploadedId(null)}
      />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate("/sourcex")}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> SourceX
            </button>
            <h1 className="text-base font-semibold tracking-tight truncate">
              Step 1 · Upload Protocol · {studyId}
            </h1>
          </div>
          {/* Only once something has been uploaded. On an empty study the one
              big button in the middle of the page IS the upload control, and a
              second one up here would split the only decision on the screen in
              two. */}
          {!loading && list.length > 0 && (
            <UploadButton
              busy={busy}
              onFile={handleUpload}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white transition ${
                busy
                  ? "bg-white/10"
                  : "bg-white/[0.06] border border-white/12 hover:bg-white/[0.12]"
              }`}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Uploading…" : "Upload another"}
            </UploadButton>
          )}
        </div>

        <NewVersionDialog
          open={!!askVersion}
          fileName={askVersion?.file?.name}
          current={askVersion?.current}
          next={askVersion?.next}
          isAdmin={isAdmin}
          onYes={() => uploadProtocolFile(askVersion.file, true)}
          onNo={() => uploadProtocolFile(askVersion.file, false)}
          onCancel={() => setAskVersion(null)}
        />
        {versionNote && (
          <div role="status" className="mb-4 text-sm text-slate-300">
            {versionNote}
          </div>
        )}
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : list.length === 0 ? (
          // The empty state IS the page: one thing to do, in the middle of the
          // screen, no icon and no list of nothing underneath it.
          <div className="flex flex-col items-center justify-center text-center py-24">
            <UploadButton
              busy={busy}
              onFile={handleUpload}
              className={`inline-flex items-center justify-center px-12 py-7 rounded-3xl text-lg font-bold text-white transition ${
                busy
                  ? "bg-white/10"
                  : "bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_18px_56px_-12px_rgba(16,185,129,0.8)] hover:shadow-[0_22px_68px_-10px_rgba(16,185,129,0.95)] hover:brightness-110"
              }`}
            >
              {busy && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
              {busy ? "Uploading…" : "Upload Protocol PDF"}
            </UploadButton>
            <p className="mt-4 text-xs text-slate-500">
              Its schedule of assessments is extracted for you to review.
            </p>
            {PROTOCOL_COMPARE_ENABLED && (
              <p className="mt-1 text-xs text-slate-500">
                This will be Version 1 of the protocol{isAdmin ? "." : ", once an admin approves it."}
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl divide-y divide-white/5 overflow-hidden">
            {list.map((p) => (
              <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3">
                {/* Three tiers, loudest first: what the file is, what state it
                    is in and what was found in it, then who put it there. They
                    used to be one flat row of same-sized grey text in which the
                    SOA pages, the timestamp and the uploader's email all looked
                    like the same kind of fact. */}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {p.fileName || p.id}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    {PROTOCOL_COMPARE_ENABLED && versionLabel(p, versions, versionRequests) && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-md border font-bold ${
                          guidance?.roleOf(p).current && (guidance?.current?.version || 0) >= 2
                            ? "protocol-version-current"
                            : "protocol-version-chip border-purple-400/30 bg-purple-500/10 text-purple-200"
                        }`}
                      >
                        {versionLabel(p, versions, versionRequests)}
                        {guidance?.roleOf(p).current && (guidance?.current?.version || 0) >= 2 ? " · Current" : ""}
                      </span>
                    )}
                    <StatusBadge status={p.status} />
                    <SoaPagesChip pages={p.soaPages} />
                  </div>
                  {p.status === PROTOCOL_STATUS.FAILED && p.errorMessage && (
                    // Its own line: an error message is a sentence, and inline
                    // beside the badges it was truncated to nothing useful.
                    <div className="mt-1.5 text-[11px] text-red-300/90">
                      {p.errorMessage}
                    </div>
                  )}
                  {/* When, and by whom. Both were already written on every
                      upload (createdBy / uploadedAt); nothing showed them. */}
                  <UploadedLine protocol={p} />
                  {guidance?.roleOf(p).locked && (
                    <div className="mt-1 text-[11px] text-slate-400">
                      Older version. Version {guidance.current.version} is the current one, so work on that.
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.status === PROTOCOL_STATUS.UPLOADED && !isStalled(p) ? (
                    // Never read: a new version is stored without reading, so
                    // this starts the reader for the first time (it uses AI).
                    <div className="flex flex-col items-end gap-0.5">
                      <button
                        onClick={() => {
                          if (guidance) logProtocolEvent(studyId, { type: "protocol_read", version: guidance.roleOf(p).version || null, protocolId: p.id, detail: p.fileName || "" });
                          handleRetry(p.id);
                        }}
                        disabled={!!guidance?.roleOf(p).locked}
                        title={guidance?.roleOf(p).locked ? `Version ${guidance.current.version} is the current version` : "Reads its schedule of assessments with AI"}
                        className={
                          guidance?.roleOf(p).prioritize
                            ? "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_8px_24px_-8px_rgba(16,185,129,0.8)] text-sm font-bold text-white hover:brightness-110 transition"
                            : "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-200 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/[0.04]"
                        }
                      >
                        <ScanSearch className={guidance?.roleOf(p).prioritize ? "w-4 h-4" : "w-3.5 h-3.5"} /> Read this protocol
                      </button>
                      {!guidance?.roleOf(p).locked && <span className="text-[10px] text-slate-500">Uses AI</span>}
                    </div>
                  ) : (
                    (p.status === PROTOCOL_STATUS.FAILED || isStalled(p)) && (
                      <button
                        onClick={() => handleRetry(p.id)}
                        disabled={!!guidance?.roleOf(p).locked}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Retry
                      </button>
                    )
                  )}
                  {/* Review and View go to the same page, and used to look
                      identical. They are not the same errand: Review is work
                      nobody else can do and the study is blocked until it
                      happens, View is looking at something already signed off.
                      So Review is filled, ringed and reads as the action on the
                      row; View is an outline. */}
                  {isReviewable(p) &&
                    (p.status === PROTOCOL_STATUS.APPROVED ? (
                      <button
                        onClick={() => navigate(`/sourcex/protocols/${studyId}/${p.id}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/12 text-xs text-slate-300 hover:bg-white/[0.1] hover:text-white transition"
                      >
                        <ScanSearch className="w-3.5 h-3.5" /> View
                      </button>
                    ) : guidance?.roleOf(p).locked ? (
                      <button
                        disabled
                        title={`Version ${guidance.current.version} is the current version`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-xs text-slate-500 opacity-60 cursor-not-allowed"
                      >
                        <ScanSearch className="w-3.5 h-3.5" /> Review
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate(`/sourcex/protocols/${studyId}/${p.id}`)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 ring-2 ring-purple-300/40 shadow-[0_8px_24px_-8px_rgba(139,92,246,0.8)] text-sm font-bold text-white hover:brightness-110 transition"
                      >
                        <ScanSearch className="w-4 h-4" /> Review
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The way out, pinned under the list once there is anything here. The
            landing modal says the same thing, but it is dismissable and this is
            not — somebody who closed it and scrolled still has one click to
            wherever they meant to be. */}
        {!loading && (list.length > 0 || hasAssembled) && (
          // Solid green once the documents exist, tinted otherwise. The two are
          // different errands wearing one shape: "carry on to the next step" is
          // a signpost, while "the documents you came to build already exist"
          // is the destination — and on THIS page it is competing with a whole
          // list of protocols and an upload button, so it has to be the loudest
          // thing here, exactly as it is on the SourceX home page.
          <motion.button
            whileHover={hasAssembled ? { y: -2 } : undefined}
            whileTap={hasAssembled ? { scale: 0.99 } : undefined}
            onClick={() => navigate(nextStep.to)}
            className={`mt-4 w-full flex items-center gap-3 px-5 rounded-2xl transition text-left ${
              hasAssembled
                ? "py-5 bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_14px_44px_-10px_rgba(16,185,129,0.75)] hover:shadow-[0_18px_56px_-8px_rgba(16,185,129,0.95)] hover:brightness-110"
                : "py-4 border border-emerald-400/30 bg-emerald-400/[0.08] hover:bg-emerald-400/[0.15] hover:border-emerald-300/50"
            }`}
          >
            <span className="min-w-0 flex-1">
              <span
                className={`block font-semibold ${
                  hasAssembled ? "text-lg font-bold text-white" : "text-sm text-emerald-100"
                }`}
              >
                {nextStep.label}
              </span>
              <span
                className={`block text-[11px] mt-0.5 ${
                  hasAssembled ? "text-emerald-50/90" : "text-emerald-100/70"
                }`}
              >
                {nextStep.hint}
              </span>
            </span>
            <ChevronRight
              className={`shrink-0 ${
                hasAssembled ? "w-6 h-6 text-white" : "w-5 h-5 text-emerald-200"
              }`}
            />
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {/* Landed on step 1 with step 1 already done. Says so in one line and
            leads with the step they actually want; re-uploading is still right
            there, just not the loud option. */}
        {landingOpen && (
          <Modal
            key="landing"
            onClose={() => setLandingOpen(false)}
            title={
              hasAssembled
                ? "Source documents are already built."
                : "A protocol is already uploaded."
            }
            footer={
              <>
                <UploadButton
                  busy={busy}
                  onFile={handleUpload}
                  className={`${quietBtn} ${busy ? "opacity-60" : ""}`}
                >
                  Re-upload
                </UploadButton>
                {offerReview ? (
                  <button
                    onClick={() => {
                      setLandingOpen(false);
                      navigate(reviewRoute);
                    }}
                    className={secondaryBtn}
                  >
                    Review protocol
                  </button>
                ) : (
                  // Not reviewable yet (usually still being read), so neither
                  // `offerReview` nor nextStep points at it. Offer it anyway —
                  // otherwise the only actionable button on a study that HAS a
                  // protocol is "Re-upload", which is the opposite of the advice.
                  existingRoute &&
                  nextStep.to !== existingRoute && (
                    <button
                      onClick={() => {
                        setLandingOpen(false);
                        navigate(existingRoute);
                      }}
                      className={secondaryBtn}
                    >
                      Open this protocol
                    </button>
                  )
                )}
                <button
                  onClick={() => {
                    setLandingOpen(false);
                    navigate(nextStep.to);
                  }}
                  className={primaryBtn}
                >
                  {nextStep.label} <ChevronRight className="w-4 h-4" />
                </button>
              </>
            }
          >
            {offerReview && <p>A protocol is still waiting to be reviewed.</p>}
            {!offerReview && existing && (
              <p>
                {existing.fileName || "A protocol"} was uploaded
                {existingWho ? ` by ${existingWho}` : ""}
                {existingWhen ? ` on ${existingWhen}` : ""}.
                {existingIsReading
                  ? " Its schedule of assessments is still being read — this takes a few minutes, and you do not need to upload it again."
                  : ""}
              </p>
            )}
          </Modal>
        )}

        {/* Just uploaded, and the extraction is DONE. Waiting and failure are
            the overlay's job now (see StepProgressOverlay at the top of this
            file), so all that is left here is the handoff: the thing is ready,
            go and read it. */}
        {uploaded && isReviewable(uploaded) && (
          <Modal
            key="uploaded"
            onClose={() => setJustUploadedId(null)}
            title="Protocol ready to review."
            footer={
              <>
                <button onClick={() => setJustUploadedId(null)} className={quietBtn}>
                  Close
                </button>
                <button
                  onClick={() => {
                    setJustUploadedId(null);
                    navigate(`/sourcex/protocols/${studyId}/${uploaded.id}`);
                  }}
                  className={primaryBtn}
                >
                  Review protocol <ChevronRight className="w-4 h-4" />
                </button>
              </>
            }
          >
            <p>Check the extracted schedule of assessments before it is used.</p>
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-500/15 px-3 py-2.5 text-sm text-amber-100">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
              <span>
                <strong className="font-semibold">AI can make mistakes.</strong> This table was read from the
                protocol by AI. Review the <strong className="font-semibold">entire Schedule of Assessments
                table</strong> against the protocol before approving it, and don't rely on it as-is.
              </span>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}
